import { useCallback, useState } from "react";
import type { HandledApiResponse } from "../api/axiosHelper";
import { withErrorHandling } from "../api/axiosHelper";
import type { ApiError, ApiResponse } from "../api/types";
import type { AsyncFunc, FuncArgs } from "../types";

interface Result<TData> {
  request: (...args: FuncArgs) => Promise<HandledApiResponse<TData>>;
  error: ApiError | undefined;
  isLoading: boolean;
}

const useRequest = <TData>(apiCall: AsyncFunc<ApiResponse<TData>>): Result<TData> => {
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);

  const request: (...args: FuncArgs) => Promise<HandledApiResponse<TData>> = useCallback(
    async (...args) => {
      setIsLoading(true);
      setError(undefined);

      const [responseData, responseError] = await withErrorHandling(apiCall)(args);

      if (responseError) {
        setError(responseError.isCanceledRequest ? undefined : responseError);
        setIsLoading(false);
        return [undefined, responseError];
      }

      setIsLoading(false);
      return [responseData, undefined];
    },
    [apiCall]
  );

  return { request, error, isLoading };
};

export default useRequest;
